import {
	Component,
	ViewChild,
	AfterViewInit,
	DoCheck,
	ChangeDetectorRef,
	DestroyRef,
	inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from 'src/app/services/api.services';
import {
	BannerMessageService,
	BannerMessageType,
} from 'src/app/services/banner-message.service';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import { AddClientConfirmationComponent } from './add-client-confirmation/add-client-confirmation.component';
import {
	AddClientDetailsComponent,
	EditClient,
	emptyEditClient,
} from './add-client-details/add-client-details.component';
import {
	AddClientPropertySetupComponent,
	PropertySelectionType,
} from './add-client-property-setup/add-client-property-setup.component';
import AddClientWithNewPropertyRequestBody = Components.RequestBodies.AddClientWithNewPropertyRequestBody;
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AppDialogWithButtonsComponent } from '../../components/app-dialog-with-buttons/app-dialog-with-buttons.component';
import { Country } from '../../models/country';
import { AppNavigationHeaderComponent } from '../../components/navigation-header/app-navigation-header.component';
import { BreadcrumbLabelDirective } from '../../directives/breadcrumb-label.directive';
import { AccessHorizontalStepperLabelsComponent } from '../../access-horizontal-stepper/access-horizontal-stepper-labels.component';

@Component({
	selector: 'app-add-client',
	templateUrl: './add-client-main-tab.component.html',
	styleUrls: ['./add-client-main-tab.component.scss'],
	imports: [
		AppNavigationHeaderComponent,
		BreadcrumbLabelDirective,
		AccessHorizontalStepperLabelsComponent,
		AddClientDetailsComponent,
		AddClientPropertySetupComponent,
		AddClientConfirmationComponent,
	],
})
export class AddClientMainTabComponent implements AfterViewInit, DoCheck {
	addClientBreadcrumbLabel = $localize`:@@addClientMainTab-addClientBreadcrumbLabel:Add Client`;

	get steps(): string[] {
		// If property is preselected, skip the Property Details step
		if (this.preselectedLocationId) {
			return [
				$localize`:@@addClientMainTab-clientDetails:Client Details`,
				$localize`:@@addClientMainTab-confirmation:Confirmation`,
			];
		}
		return [
			$localize`:@@addClientMainTab-clientDetails:Client Details`,
			$localize`:@@addClientMainTab-propertyDetails:Property Details`,
			$localize`:@@addClientMainTab-confirmation:Confirmation`,
		];
	}

	currentStep: number = 0;
	enableContinueButton: boolean = false;

	editClient: EditClient = emptyEditClient();
	propertySelectionType: PropertySelectionType | undefined;
	selectedProperty?: any; // PropertyResult from property-setup component
	preselectedLocationId?: string; // Property preselected from query param

	serverCallInProgress = false;

	@ViewChild(AddClientConfirmationComponent)
	addClientConfirmation?: AddClientConfirmationComponent;
	@ViewChild(AddClientDetailsComponent)
	addClientDetails?: AddClientDetailsComponent;
	@ViewChild(AddClientPropertySetupComponent)
	addClientPropertySetup?: AddClientPropertySetupComponent;

	private readonly destroyRef = inject(DestroyRef);

	constructor(
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly apiService: ApiService,
		private readonly bannerService: BannerMessageService,
		private readonly navigationHistory: NavigationHistoryService,
		private readonly cdr: ChangeDetectorRef,
		private readonly dialog: MatDialog
	) {}

	async ngAfterViewInit(): Promise<void> {
		// Check for preselected property from query params
		this.route.queryParams
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(async (params) => {
				if (params['locationId']) {
					this.preselectedLocationId = params['locationId'];
					await this.loadPreselectedProperty(params['locationId']);
				}
			});
	}

	async loadPreselectedProperty(locationId: string): Promise<void> {
		try {
			const property = await this.apiService.getProperty(locationId);
			this.selectedProperty = { property };
			this.propertySelectionType = PropertySelectionType.existing;
		} catch (error) {
			this.bannerService.sendMessage(
				$localize`:@@addClientMainTab-propertyLoadError:Could not load the selected property.`,
				BannerMessageType.error
			);
		}
	}

	ngDoCheck(): void {
		// Update continue button state whenever there are changes
		this.updateContinueButtonState();
	}

	private updateContinueButtonState(): void {
		switch (this.currentStep) {
			case 0:
				this.enableContinueButton = this.addClientDetails?.isValid ?? false;
				break;
			case 1:
				// If property is preselected, step 1 is confirmation; otherwise it's property setup
				if (this.preselectedLocationId) {
					this.enableContinueButton = false; // No continue button on confirmation
				} else {
					this.enableContinueButton =
						this.addClientPropertySetup?.isValid ?? false;
				}
				break;
			case 2:
				this.enableContinueButton = false; // No continue button on confirmation
				break;
			default:
				this.enableContinueButton = false;
		}
	}

	onPressBack(): void {
		if (this.preselectedLocationId && this.currentStep === 1) {
			// If property is preselected, step 1 is confirmation, go back to step 0
			this.currentStep = 0;
			// Trigger change detection to ensure ViewChild is updated and button state is refreshed
			this.cdr.detectChanges();
			return;
		}

		if (this.currentStep === 1) {
			// If user has confirmed selection, go back to radio buttons (Phase 2 -> Phase 1)
			if (this.addClientPropertySetup?.hasConfirmedSelection) {
				// Keep the radio selection (selectedType) but clear the confirmed action
				this.addClientPropertySetup.hasConfirmedSelection = false;
				// Clear the property selection from Phase 2
				this.propertySelectionType = undefined;
				this.selectedProperty = undefined;
				this.addClientPropertySetup.selectedProperty = undefined;
				this.addClientPropertySetup.showPropertyDetails = false;
				this.addClientPropertySetup.propertySearchControl.setValue('');
				this.addClientPropertySetup.addClientWithNewPropertyForm?.reset();
				return;
			}
			// Otherwise, go back to previous step (Phase 1 -> Step 0)
			// Clear all Step 1 data
			this.propertySelectionType = undefined;
			this.selectedProperty = undefined;
			if (this.addClientPropertySetup) {
				this.addClientPropertySetup.selectedType = undefined;
				this.addClientPropertySetup.hasConfirmedSelection = false;
				this.addClientPropertySetup.selectedProperty = undefined;
				this.addClientPropertySetup.showPropertyDetails = false;
			}
			this.currentStep--;
			// Trigger change detection to ensure ViewChild is updated and button state is refreshed
			this.cdr.detectChanges();
		}
	}

	async onPressContinue(): Promise<void> {
		if (!this.enableContinueButton) {
			return;
		}

		switch (this.currentStep) {
			case 0:
				// If property is preselected, create client and go to step 1 which is now confirmation (property step is hidden)
				if (this.preselectedLocationId) {
					await this.createClient();
				}
				this.currentStep++; // Go to next step no matter what
				break;
			case 1:
				// Check if this is the first Continue press (confirming radio selection)
				if (
					this.addClientPropertySetup &&
					!this.addClientPropertySetup.hasConfirmedSelection
				) {
					this.addClientPropertySetup.confirmSelection();
					// Stay on same step to show property search/form
					return;
				}

				// Handle based on property selection type (second Continue press)
				if (this.propertySelectionType === PropertySelectionType.newProperty) {
					await this.createClientWithNewProperty(false);
					return;
				} else {
					await this.createClient();
				}
				// Move to confirmation
				this.currentStep++;
				break;
			case 2:
				// This shouldn't happen as continue button is hidden on confirmation
				break;
		}
	}

	onPropertySelectionTypeChanged(type: PropertySelectionType): void {
		this.propertySelectionType = type;
		// Clear selected property when type changes
		this.selectedProperty = undefined;
	}

	onPropertySelected(property: any | undefined): void {
		this.selectedProperty = property;
	}

	async createClient(): Promise<void> {
		// make sure the user can't create the client multiple times from clicking the button multiple times quickly
		if (!this.serverCallInProgress) {
			this.serverCallInProgress = true;

			try {
				const clientData = {
					firstName: this.editClient.firstName?.trim(),
					lastName: this.editClient.lastName?.trim(),
					idn: this.editClient.idn?.trim(),
					locationId: this.selectedProperty?.property?.id,
					active: true,
				};
				await this.apiService.createNewClient(clientData);
				this.serverCallInProgress = false;
			} catch (err) {
				this.serverCallInProgress = false;
				this.bannerService.sendMessage(
					$localize`:@@addClientConfirmation-failureMessage:There was an issue while creating the Client. Please check the data and try again.`,
					BannerMessageType.error
				);
			}
		}
	}

	async createClientWithNewProperty(isConfirmed: boolean): Promise<void> {
		try {
			const propertyData =
				this.addClientPropertySetup?.addClientWithNewPropertyForm.getRawValue();
			let countryName;
			if (typeof propertyData?.country === 'string') {
				countryName = propertyData?.country;
			}
			if (typeof propertyData?.country === 'object') {
				countryName = propertyData?.country?.twoLetterCode;
			}

			let timeZone;
			if (typeof propertyData?.timezone === 'string') {
				timeZone = propertyData?.timezone;
			}
			if (typeof propertyData?.timezone === 'object') {
				timeZone = propertyData?.timezone?.timezone;
			}

			const clientData: AddClientWithNewPropertyRequestBody = {
				firstName: this.editClient.firstName?.trim(),
				lastName: this.editClient.lastName?.trim(),
				idn: this.editClient.idn?.trim(),
				propertyName: propertyData?.propertyName?.trim(),
				timezone: timeZone,
				address: propertyData?.address?.trim(),
				apartment: propertyData?.apartment?.trim(),
				town: propertyData?.town?.trim(),
				state: propertyData?.state?.trim(),
				country: countryName,
				postalCode: propertyData?.postalCode?.trim(),
			};
			const result = await this.apiService.createClientWithNewProperty(
				clientData,
				isConfirmed
			);

			// Property exists with same address and apartment/suite
			if ('existingProperty' in result) {
				const existingProperty: any = result.existingProperty;
				const propertyName = existingProperty?.propertyName;
				const address =
					existingProperty?.address +
					', ' +
					existingProperty?.locality +
					', ' +
					existingProperty?.postalCode;

				const dialogConfig = new MatDialogConfig();
				dialogConfig.panelClass = 'dialog-container';
				dialogConfig.data = {
					title: $localize`:@@clientDetails-existingPropertyDialogTitle:Property Found`,
					message: $localize`:@@clientDetails-existingPropertyDialogDescription:We found a property with this address already in the system. `,
					cancelButtonText: $localize`:@@clientDetails-existingPropertyDialogCancelButtonTitle:Create New`,
					confirmButtonText: $localize`:@@clientDetails-existingPropertyDialogConfirmButtonTitle:Use Existing Property`,
					htmlContent: `
                        <h3>Existing Property:</h3>
                        <ul>
                            <li data-automation-id="dialog-property-name"><b i18n="@@addClientMainTab-existingPropertyDialogName">Name:</b> ${propertyName}</li>
                            <li data-automation-id="dialog-property-address"><b i18n="@@addClientMainTab-existingPropertyDialogAddress">Address:</b> ${address}</li>
                        </ul>
                        `,
				};
				const dialog = this.dialog.open(
					AppDialogWithButtonsComponent,
					dialogConfig
				);

				dialog
					.afterClosed()
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe(
						async (closeDialogWith: {
							isCancelButtonClicked: boolean;
							isConfirmButtonClicked: boolean;
						}) => {
							// Update selected property to existing property
							this.selectedProperty = {
								property: {
									id: existingProperty?.locationId,
									name: existingProperty?.propertyName,
									address: {
										address1: existingProperty?.address || '',
										address2: existingProperty?.apartment,
										locality: existingProperty?.locality || '',
										stateProvinceRegion: existingProperty?.state,
										postalCode: existingProperty?.postalCode,
										countryCode: existingProperty?.contryCode,
									},
								},
							};

							if (closeDialogWith?.isCancelButtonClicked) {
								this.createClientWithNewProperty(
									closeDialogWith.isCancelButtonClicked
								);

								this.bannerService.sendMessage(
									$localize`:@@addClientConfirmation-successMessage:Successfully added Client`,
									BannerMessageType.success
								);

								this.currentStep++;
								return;
							}

							if (closeDialogWith?.isConfirmButtonClicked) {
								this.createClient();

								this.bannerService.sendMessage(
									$localize`:@@addClientConfirmation-successMessage:Successfully added Client`,
									BannerMessageType.success
								);

								this.currentStep++;
								return;
							}
						}
					);
			} else {
				if (!isConfirmed) {
					this.selectedProperty = {
						property: {
							id: '',
							name: result?.propertyName,
							address: {
								address1: result?.address || '',
								address2: result?.apartment,
								locality: result?.town || '',
								stateProvinceRegion: result?.state,
								postalCode: result?.postalCode,
								countryCode: result?.country,
							},
						},
					};

					this.currentStep++;
				}

				this.bannerService.sendMessage(
					$localize`:@@addClientConfirmation-successMessage:Successfully added Client`,
					BannerMessageType.success
				);
			}
		} catch (err) {
			this.bannerService.sendMessage(
				$localize`:@@addClientConfirmation-failureMessage:There was an issue while creating the Client. Please check the data and try again.`,
				BannerMessageType.error
			);
		}
	}

	onReturnToClients(): void {
		// If coming from a property, return to that property's Clients tab
		if (this.preselectedLocationId) {
			this.router.navigate(['/property-details', this.preselectedLocationId], {
				queryParams: { openTab: 'clients' },
			});
		} else {
			this.router.navigate(['/clients']);
		}
	}

	onAddAnotherClient(): void {
		// Reset the form and start over
		this.currentStep = 0;
		this.editClient = emptyEditClient();
		this.propertySelectionType = undefined;
		this.selectedProperty = undefined;
		this.addClientPropertySetup = undefined;
	}
}
